// src/components/PlayersMergePanel.jsx
// Onglet ADMIN : fusion de joueurs en doublon
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { refs } from '../data/refs';
import {
  getAllPlayersWithCounts, mergePlayers, normalizeText, sortedKey,
} from '../services/playersService';

const classementLabel = (code) => {
  if (!code) return '';
  const f = refs.refClassement.find(c => c.code === String(code).trim());
  return f ? f.labelFr : code;
};
const fullName = (p) => [p.prenom, p.nom].filter(Boolean).join(' ') || '(sans nom)';

export default function PlayersMergePanel() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState([]);   // max 2 ids
  const [confirm, setConfirm] = useState(null);   // { keepId }
  const [merging, setMerging] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await getAllPlayersWithCounts();
    if (r.success) { setPlayers(r.players); setError(''); }
    else setError(r.error);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Groupes de doublons "exacts" (même nom/prénom, accents et ordre ignorés)
  const suspectGroups = useMemo(() => {
    const map = {};
    players.forEach((p) => {
      const k = sortedKey(p.nom, p.prenom);
      if (!k) return;
      (map[k] = map[k] || []).push(p);
    });
    return Object.values(map).filter(g => g.length > 1);
  }, [players]);

  const filtered = useMemo(() => {
    const tokens = normalizeText(filter).split(' ').filter(Boolean);
    const list = tokens.length
      ? players.filter(p => {
          const full = normalizeText(`${p.prenom || ''} ${p.nom || ''}`);
          return tokens.every(t => full.includes(t));
        })
      : players;
    return list.slice(0, 200);
  }, [players, filter]);

  const toggle = (id) => {
    setMessage('');
    setSelected(prev => prev.includes(id)
      ? prev.filter(x => x !== id)
      : prev.length >= 2 ? [prev[1], id] : [...prev, id]);
  };

  const selPlayers = selected.map(id => players.find(p => p.id === id)).filter(Boolean);

  const openConfirm = () => {
    if (selPlayers.length !== 2) return;
    // Par défaut on conserve la fiche qui a le plus de matchs, puis la plus ancienne
    const [a, b] = selPlayers;
    let keep = a;
    if (b.matchCount > a.matchCount) keep = b;
    else if (b.matchCount === a.matchCount && new Date(b.createdAt) < new Date(a.createdAt)) keep = b;
    setConfirm({ keepId: keep.id });
  };

  const doMerge = async () => {
    const keep = selPlayers.find(p => p.id === confirm.keepId);
    const drop = selPlayers.find(p => p.id !== confirm.keepId);
    setMerging(true);
    const r = await mergePlayers(keep.id, drop.id);
    setMerging(false);
    setConfirm(null);
    if (r.success) {
      const res = r.result || {};
      setMessage(`✅ ${fullName(drop)} fusionné dans ${fullName(keep)} — ${res.matchesMoved ?? 0} match(s) réaffecté(s), ${(res.linksMoved ?? 0) + (res.linksMerged ?? 0)} liaison(s) traitée(s).`);
      setSelected([]);
      await load();
    } else {
      setError(r.error);
    }
  };

  const th = { textAlign: 'left', padding: '6px 6px', fontSize: '12px', color: '#666', borderBottom: '2px solid #ddd', whiteSpace: 'nowrap' };
  const td = { padding: '7px 6px', fontSize: '13px', borderBottom: '1px solid #eee', verticalAlign: 'middle' };

  return (
    <div>
      <p style={{ fontSize: '13px', color: '#555', marginTop: 0 }}>
        Cochez <strong>2 joueurs</strong> représentant la même personne, puis fusionnez.
        Les matchs et les listes des utilisateurs seront rattachés à la fiche conservée.
      </p>

      {error && <div style={{ backgroundColor: '#ffebee', color: '#c62828', padding: '8px', borderRadius: '6px', fontSize: '13px', marginBottom: '10px' }}>{error}</div>}
      {message && <div style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', padding: '8px', borderRadius: '6px', fontSize: '13px', marginBottom: '10px' }}>{message}</div>}

      {suspectGroups.length > 0 && (
        <div style={{ backgroundColor: '#fff8e1', border: '1px solid #ffe082', borderRadius: '8px', padding: '10px', marginBottom: '12px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>
            ⚠️ {suspectGroups.length} doublon(s) probable(s) détecté(s)
          </div>
          {suspectGroups.map((g, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', padding: '3px 0' }}>
              <span>{fullName(g[0])} <span style={{ color: '#888' }}>({g.length} fiches)</span></span>
              <button
                type="button"
                onClick={() => { setMessage(''); setSelected([g[0].id, g[1].id]); }}
                style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #ffb300', backgroundColor: '#fff', cursor: 'pointer', fontSize: '12px' }}
              >
                Sélectionner
              </button>
            </div>
          ))}
        </div>
      )}

      <input
        type="text"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="🔍 Filtrer par nom ou prénom..."
        style={{ width: '100%', padding: '9px 10px', borderRadius: '8px', border: '1px solid #ccc', fontSize: '14px', boxSizing: 'border-box', marginBottom: '10px' }}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontSize: '13px', color: '#666' }}>{selected.length}/2 sélectionné(s)</span>
        <button
          type="button"
          disabled={selected.length !== 2}
          onClick={openConfirm}
          style={{
            padding: '8px 14px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: 600,
            backgroundColor: selected.length === 2 ? '#d32f2f' : '#ccc', color: '#fff',
            cursor: selected.length === 2 ? 'pointer' : 'default',
          }}
        >
          🔀 Fusionner
        </button>
      </div>

      {loading ? <p style={{ textAlign: 'center', color: '#888' }}>Chargement...</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}></th>
                <th style={th}>Joueur</th>
                <th style={th}>Class.</th>
                <th style={th}>Club</th>
                <th style={th}>Matchs</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} style={{ backgroundColor: selected.includes(p.id) ? '#e3f2fd' : 'transparent' }} onClick={() => toggle(p.id)}>
                  <td style={td}><input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggle(p.id)} /></td>
                  <td style={td}><strong>{fullName(p)}</strong></td>
                  <td style={td}>{classementLabel(p.dernierClassementCode)}</td>
                  <td style={td}>{p.dernierClub || ''}</td>
                  <td style={td}>{p.matchCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {players.length > filtered.length && !filter && (
            <p style={{ fontSize: '12px', color: '#888' }}>Affichage limité à 200 joueurs : utilisez le filtre.</p>
          )}
        </div>
      )}

      {confirm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }}>
          <div style={{ backgroundColor: '#fff', color: '#222', borderRadius: '12px', padding: '16px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '17px' }}>🔀 Fusionner ces 2 joueurs</h3>
            <p style={{ fontSize: '13px', color: '#555', margin: '0 0 10px' }}>Choisissez la fiche à <strong>conserver</strong> :</p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr><th style={th}>Garder</th><th style={th}>Joueur</th><th style={th}>Class.</th><th style={th}>Club</th><th style={th}>Matchs</th></tr>
                </thead>
                <tbody>
                  {selPlayers.map(p => (
                    <tr key={p.id} style={{ backgroundColor: confirm.keepId === p.id ? '#e8f5e9' : 'transparent' }}>
                      <td style={td}><input type="radio" name="keep" checked={confirm.keepId === p.id} onChange={() => setConfirm({ keepId: p.id })} /></td>
                      <td style={td}><strong>{fullName(p)}</strong></td>
                      <td style={td}>{classementLabel(p.dernierClassementCode)}</td>
                      <td style={td}>{p.dernierClub || ''}</td>
                      <td style={td}>{p.matchCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ fontSize: '13px', color: '#b71c1c', margin: '12px 0' }}>
              L'autre fiche sera <strong>supprimée</strong> : ses matchs et ses liaisons seront rattachés à la fiche conservée. Les champs vides de la fiche conservée seront complétés. Action irréversible.
            </p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" onClick={() => setConfirm(null)} disabled={merging}
                style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #bbb', backgroundColor: '#f5f5f5', cursor: 'pointer', fontSize: '14px' }}>
                Annuler
              </button>
              <button type="button" onClick={doMerge} disabled={merging}
                style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', backgroundColor: '#d32f2f', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>
                {merging ? 'Fusion...' : 'Confirmer la fusion'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}